import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateCoreAccess } from '@/hooks/useCoreAccess';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { env } from '@/lib/env';
import { useAuth } from './useAuth';
import {
  clearCheckoutAttributionContext,
  storeCheckoutAttributionContext,
} from '@/utils/checkoutAttribution';
import { getNativeTrackingContext, resolvePurchaseStore } from '@/utils/nativeTrackingContext';
import {
  getCheckoutAttributionPayload,
  getInstallSessionIdForAttribution,
  isClientConversionTracked,
  markClientConversionTracked,
  trackEvent,
} from '@/utils/trackingManager';
import {
  ANNUAL_LED_TRIAL_DAYS,
  getNativeAnnualSaveOfferProductId,
  canonicalNativeStore,
  extractNativeSubscriptionSnapshot,
  getNativeCustomerInfo,
  getNativeEntitlementId,
  getNativeStoreManagementUrlFallback,
  isNativeAnnualSaveOfferSourceProductId,
  isNativeAnnualSaveOfferTargetProductId,
  isKnownNativePremiumProductId,
  isNativePurchaseAlreadyOwnedError,
  isNativeBillingEnabled,
  isNativePurchaseCanceledError,
  openNativeSubscriptionManagementUrl,
  purchaseAnnualLedPlan,
  purchaseAnnualLedPlanWithPrice,
  purchaseNativeAnnualTrialMonthlyPlan,
  getNativeAnnualMonthlyPaidQuote,
  purchaseNativeAnnualMonthlyPaidPlan,
  purchaseNativeAnnualSaveOffer,
  purchaseNativePaidFirstRescueTrial,
  purchaseNativeOnboardingYearlyOffer,
  purchaseNativeSubscription,
  syncNativeCheckoutAttribution,
  restoreNativeSubscriptionPurchases,
  isNativePurchaseFlowEnabled,
  type NativeSubscriptionSnapshot,
  PAID_FIRST_RESCUE_TRIAL_DAYS,
} from '@/services/nativeBilling';
import {
  getEntitlementPeriodType,
  isEvidencedTrialPeriod,
  isTrialOfferKind,
  nativeConversionTracking,
} from '@/utils/nativeTrialTracking';
import {
  nativeNamedCheckoutDedupeKey,
  nativePurchaseDedupeCandidates,
  nativePurchaseDedupeKey,
} from '@/utils/nativePurchaseDedupe';
import { isNativeBillingLoadingQaForced } from '@/utils/qaSubscriptionFlags';
import {
  recordNativeSaveOffer,
  savePendingNativeRescueRecord,
  type RecordNativeSaveOfferInput,
} from '@/services/saveOffers';
import type { CancellationReason } from '@/components/composites/CancelFlowModal';
import {
  type BillingTier,
  type Invoice,
  type SubscriptionInfo,
  type SubscriptionRecord,
  ONBOARDING_YEARLY_OFFER_PRICE,
  LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE,
  PRICING_TIERS,
  deriveSubscriptionState,
  vipProFallbackValue,
  normalizeBillingTier,
  normalizeSubscriptionTier,
  memberPlanTierFor,
} from '@/domain/subscription';
import { buildExternalWebUrl } from '@/utils/publicShareUrls';
import { buildJourneyCheckoutUrls, getDirectJourneyId, getJourneyEntryDay } from '@/utils/journeyEntry';
import { resolveStartIntentCheckoutReturn } from '@/utils/startGate';
import {
  buildOnboardingHostedCheckoutUrls,
  isOnboardingHostedCheckout,
} from '@/utils/onboardingCheckout';
import { isNativeCancelOfferStatus } from '@/utils/nativeCancelOfferStatus';
import {
  buildVipProCheckoutDetails,
  resolveNativeVipProStoreProductId,
} from '@/utils/vipProCheckoutTracking';

/**
 * v7 useSubscription - Manages premium subscription state
 *
 * Per UX Spec v7.1 and Epic 8:
 * - Story 8.1: Premium value proposition
 * - Story 8.2: Stripe checkout integration
 * - Story 8.5: Subscription management
 * - Story 8.6: Billing history
 *
 * Pure billing rules (tier resolution, provider precedence, pricing) live in
 * src/domain/subscription — this hook owns IO and React state only, and
 * re-exports the domain surface so existing imports keep working.
 */

export * from '@/domain/subscription';

/**
 * Extract a useful message from Supabase FunctionsHttpError context
 */
async function extractFunctionErrorMessage(error: unknown): Promise<string> {
  if (error && typeof error === 'object' && 'context' in error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const body = await context.clone().json();
        if (body && typeof body.error === 'string' && body.error.trim().length > 0) {
          return body.error.trim();
        }
      } catch {
        // Ignore JSON parse failures and try text fallback.
      }

      try {
        const text = await context.clone().text();
        if (text?.trim()) return text.trim();
      } catch {
        // Ignore text parse failures.
      }
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return 'Checkout failed';
}

const SESSION_EXPIRED_ERROR_MESSAGE = 'Your session expired. Please sign in again and retry.';

function isInvalidJwtErrorMessage(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('invalid jwt') ||
    normalized.includes('jwt expired') ||
    normalized.includes('token has expired')
  );
}

async function getAccessTokenOrThrow(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const validateAccessToken = async (accessToken: string | null | undefined) => {
    if (!accessToken) return false;

    const { data, error } = await supabase.auth.getUser(accessToken);
    return !error && !!data.user;
  };

  if (await validateAccessToken(session?.access_token)) {
    return session!.access_token;
  }

  const refreshSource = session?.refresh_token
    ? { refresh_token: session.refresh_token }
    : undefined;
  const { data, error } = await supabase.auth.refreshSession(refreshSource);
  if (error || !data.session?.access_token) {
    throw new Error(SESSION_EXPIRED_ERROR_MESSAGE);
  }

  const refreshedAccessToken = data.session.access_token;
  if (!(await validateAccessToken(refreshedAccessToken))) {
    throw new Error(SESSION_EXPIRED_ERROR_MESSAGE);
  }

  return refreshedAccessToken;
}

export async function invokeFunctionWithAuth<T>(
  functionName: string,
  body: Record<string, unknown> = {},
  options: {
    signOutOnInvalidSession?: boolean;
  } = {}
): Promise<T> {
  const shouldSignOutOnInvalidSession = options.signOutOnInvalidSession ?? false;

  // Dev builds only: Stripe test-mode QA against a locally served
  // create-checkout-session (production functions run live and refuse
  // non-production origins). Production builds never read this.
  const qaCheckoutUrl =
    import.meta.env.DEV && functionName === 'create-checkout-session'
      ? import.meta.env.VITE_QA_CHECKOUT_FUNCTION_URL
      : undefined;

  const invokeWithToken = async (token: string) => {
    if (!qaCheckoutUrl) {
      return supabase.functions.invoke(functionName, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body,
      });
    }
    const response = await fetch(qaCheckoutUrl, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json().catch(() => ({}));
    return response.ok
      ? { data: json, error: null }
      : { data: null, error: new Error(json?.error || `QA checkout failed (${response.status})`) };
  };

  const accessToken = await getAccessTokenOrThrow();
  const firstAttempt = await invokeWithToken(accessToken);
  if (!firstAttempt.error) {
    return firstAttempt.data as T;
  }

  const firstMessage = await extractFunctionErrorMessage(firstAttempt.error);
  if (!isInvalidJwtErrorMessage(firstMessage)) {
    throw new Error(firstMessage);
  }

  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
  const refreshedToken = refreshed.session?.access_token;
  if (refreshError || !refreshedToken) {
    throw new Error(SESSION_EXPIRED_ERROR_MESSAGE);
  }

  const retryAttempt = await invokeWithToken(refreshedToken);
  if (!retryAttempt.error) {
    return retryAttempt.data as T;
  }

  const retryMessage = await extractFunctionErrorMessage(retryAttempt.error);
  if (isInvalidJwtErrorMessage(retryMessage)) {
    if (shouldSignOutOnInvalidSession) {
      await supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
    }
    throw new Error(SESSION_EXPIRED_ERROR_MESSAGE);
  }

  throw new Error(retryMessage);
}

/**
 * Web sells one lineup on every surface, congruent with the App Store and
 * Google Play: Annual $97 with 14 days free, Monthly $12 with 7 days free,
 * and the same prices charged today once an account has used its trial.
 * Older paywall offer kinds map onto those bridge kinds so no web surface can
 * request a retired price or trial length.
 */
export function toWebCheckoutOfferKind(
  tier: BillingTier,
  offerKind: NonNullable<CheckoutTrackingContext['offerKind']>,
): string {
  switch (offerKind) {
    case 'annual_trial':
      return tier === 'annual' ? 'bridge_annual_trial' : 'bridge_monthly_trial';
    case 'monthly_trial':
      return 'bridge_monthly_trial';
    case 'annual_monthly_paid_annual':
      return 'bridge_annual_paid';
    case 'annual_monthly_paid_monthly':
      return 'standard';
    default:
      return offerKind;
  }
}

/**
 * Create Stripe checkout session
 */
async function createCheckoutSession(
  tier: BillingTier,
  successUrl: string,
  cancelUrl: string,
  trackingContext: CheckoutTrackingContext = {}
): Promise<{ url: string }> {
  return invokeFunctionWithAuth<{ url: string }>('create-checkout-session', {
    tier,
    successUrl,
    cancelUrl,
    ...(trackingContext.offerKind ? {
      offerKind: toWebCheckoutOfferKind(tier, trackingContext.offerKind),
    } : {}),
    ...(trackingContext.offerVersion ? { offerVersion: trackingContext.offerVersion } : {}),
    ...(trackingContext.entryRoute ? { entryRoute: trackingContext.entryRoute } : {}),
    trackingContext,
    attribution: getCheckoutAttributionPayload(),
  });
}

/**
 * Create Stripe customer portal session
 */
type PortalSessionResponse = {
  url?: string;
  switched?: boolean;
  scheduled?: boolean;
  effective_at?: string;
  target_subscription_type?: string;
  cancel_at_period_end?: boolean;
  current_period_end?: string;
  message?: string;
  mode?: 'test' | 'live';
  subscription_id?: string;
  subscription_type?: string;
  status?: string;
};

type PortalSessionAction = 'cancel_scheduled_change' | 'cancel_subscription';

export interface SaveOfferResponse {
  applied?: boolean;
  eligible?: boolean;
  code?: string;
  message?: string;
  offer?: string;
  percentOff?: number;
  months?: number;
  offerMonthlyCents?: number;
  regularMonthlyCents?: number;
  annualPriceCents?: number;
  permanent?: boolean;
  effectiveAt?: string;
}

export interface CheckoutTrackingContext {
  source?: string;
  paywallType?: string;
  paywallFeature?: string;
  contentId?: string;
  contentType?: string;
  contentTitle?: string;
  /**
   * Not just tracking: requests the exact offer shown by the paywall. Trial
   * eligibility is revalidated by the provider before checkout, while
   * 'onboarding_yearly' selects the separate $67 first-year offer.
   */
  offerKind?:
    | 'annual_trial'
    | 'monthly_trial'
    | 'bridge_annual_trial'
    | 'bridge_annual_paid'
    | 'bridge_monthly_trial'
    | 'onboarding_yearly'
    | 'annual_led_annual'
    | 'annual_led_weekly'
    | 'annual_monthly_paid_annual'
    | 'annual_monthly_paid_monthly'
    | 'annual_monthly_annual_trial'
    | 'annual_monthly_annual'
    | 'annual_monthly_monthly'
    | 'paid_first_rescue_trial'
    | 'annual_rescue'
    | 'paid_first_annual'
    | 'paid_first_weekly'
    // VIP/Pro (web only): the server resolves the price for its Stripe mode.
    | 'vip_upfront'
    | 'vip_rescue_annual_trial'
    | 'pro_upgrade';
  /** VIP/Pro offer version and the screen it opened from, copied to checkout metadata. */
  offerVersion?: string;
  entryRoute?: string;
  /**
   * Offer arm for measurement (see the annual-led plan's measurement
   * appendix). Propagates into checkout events and, via subscriber
   * attributes, into RevenueCat-created ledger rows.
   */
  offerConfig?:
    | 'annual_led'
    | 'annual_monthly_paid'
    | 'annual_trial_monthly'
    | 'paid_first'
    | 'bridge'
    | 'monthly_only';
  /** Displayed store price; checkout revalidates it before opening the sheet. */
  quotedPrice?: { value: number; currency: string };
  /**
   * Native save-offer rescue only (offerKind 'annual_rescue'): the pitch that
   * brought the subscriber here (from the push/email deep link) and the
   * cancel-intercept survey answer. Both are recorded with the redemption.
   */
  rescue?: {
    pitchId?: string | null;
    cancelReason?: CancellationReason | null;
  };
}

interface CheckoutHandoffOptions {
  /**
   * Runs immediately after the store confirms ownership, before any route
   * transition. Used to persist the guest-to-account handoff against the
   * exact Supabase user id that owns the RevenueCat purchase.
   */
  onPurchaseConfirmed?: () => Promise<void> | void;
  /**
   * Runs after an in-app store purchase succeeds. Hosted onboarding checkout
   * stays resumable until the provider returns with a confirmed subscription.
   */
  onHandoffReady?: () => Promise<boolean>;
  /** A caller can leave while a hosted checkout is being created. */
  canRedirect?: () => boolean;
}

const NATIVE_CHECKOUT_COMPLETION_PENDING_MESSAGE =
  'Your membership is active, but we could not finish setting up your account. Please tap the button again to continue.';

class NativeCheckoutCompletionPendingError extends Error {
  readonly code = 'native_checkout_completion_pending';

  constructor() {
    super(NATIVE_CHECKOUT_COMPLETION_PENDING_MESSAGE);
    this.name = 'NativeCheckoutCompletionPendingError';
  }
}

function isNativeCheckoutCompletionPendingError(
  error: unknown
): error is NativeCheckoutCompletionPendingError {
  return (
    error instanceof NativeCheckoutCompletionPendingError ||
    (typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      error.code === 'native_checkout_completion_pending')
  );
}

async function requireNativeCheckoutHandoff(
  handoffOptions: CheckoutHandoffOptions
): Promise<void> {
  if (!handoffOptions.onHandoffReady) return;

  try {
    const handoffRecorded = await handoffOptions.onHandoffReady();
    if (handoffRecorded === false) {
      throw new NativeCheckoutCompletionPendingError();
    }
  } catch (error) {
    if (isNativeCheckoutCompletionPendingError(error)) throw error;
    console.error('Native purchase succeeded but post-purchase setup failed:', error);
    throw new NativeCheckoutCompletionPendingError();
  }
}

async function recordNativePurchaseConfirmed(
  handoffOptions: CheckoutHandoffOptions,
): Promise<void> {
  if (!handoffOptions.onPurchaseConfirmed) return;
  try {
    await handoffOptions.onPurchaseConfirmed();
  } catch (error) {
    console.error('Native purchase succeeded but account handoff could not be saved:', error);
    throw new NativeCheckoutCompletionPendingError();
  }
}

const WEB_BILLING_SETTINGS_URL = 'https://journalparty.com/settings/subscription';
const PAYPAL_MANAGE_URL_FALLBACK =
  env.paypalEnv === 'live'
    ? 'https://www.paypal.com/myaccount/autopay/'
    : 'https://www.sandbox.paypal.com/myaccount/autopay/';
const IS_NATIVE_RUNTIME = typeof window !== 'undefined' && Capacitor.isNativePlatform();

function normalizeRelativePath(path: string | null | undefined, fallback: string): string {
  if (!path) return fallback;
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return fallback;
  }
  return trimmed;
}

/**
 * The Journey a checkout should return to, if any. The pricing page passes
 * its own path with the original destination nested as `returnTo`, so both
 * the direct form and the nested form are recognized.
 */
function resolveJourneyEntryTarget(returnPath?: string): string | null {
  const direct = getDirectJourneyId(returnPath || '');
  if (direct) return direct;
  try {
    const nested = new URL(returnPath || '', 'https://journalparty.com').searchParams.get('returnTo');
    return nested ? getDirectJourneyId(nested) : null;
  } catch {
    return null;
  }
}

function resolveNativePostCheckoutPath(returnPath?: string): string {
  const normalizedReturnPath = normalizeRelativePath(returnPath, '/welcome-premium');
  try {
    const parsed = new URL(normalizedReturnPath, 'https://journalparty.com');

    // Paywalls route through /pricing?returnTo=... on web checkout.
    // For native checkout, go straight back to the gated screen.
    if (parsed.pathname === '/pricing') {
      const returnTo = normalizeRelativePath(parsed.searchParams.get('returnTo'), '');
      if (returnTo) {
        return returnTo;
      }
      return '/welcome-premium';
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return normalizedReturnPath;
  }
}

function withCheckoutResult(path: string, result: 'success' | 'canceled'): string {
  try {
    const parsed = new URL(path, 'https://journalparty.com');
    parsed.searchParams.set('checkout', result);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}checkout=${result}`;
  }
}

function getExistingBillingCheckoutMessage(args: {
  hasBillingAccess: boolean;
  isLegacyWebSubscriber: boolean;
  provider: SubscriptionInfo['provider'];
  status: SubscriptionInfo['status'];
}): string | null {
  const hasManageableBilling =
    args.status === 'active' ||
    args.status === 'trialing' ||
    args.status === 'past_due' ||
    args.status === 'unpaid';
  if (!args.hasBillingAccess && !hasManageableBilling) return null;

  if (args.isLegacyWebSubscriber) {
    return 'Your Premium access is from web checkout. Manage billing on the web version of Journal Party.';
  }

  if (args.provider === 'native_store') {
    return 'You already have an active App Store or Play Store subscription. Manage billing in Settings.';
  }

  if (args.provider === 'paypal') {
    return 'You already have an active PayPal subscription. Manage billing in Settings.';
  }

  if (args.status === 'past_due' || args.status === 'unpaid') {
    return 'You already have a subscription with a billing issue. Manage billing in Settings before starting another checkout.';
  }

  return 'You already have an active subscription. Manage billing in Settings.';
}

async function createPortalSession(
  returnUrl: string,
  targetTier?: BillingTier,
  action?: PortalSessionAction
): Promise<PortalSessionResponse> {
  const body: Record<string, unknown> = { returnUrl };
  if (targetTier) {
    body.targetTier = targetTier;
  }
  if (action) {
    body.action = action;
  }

  return invokeFunctionWithAuth<PortalSessionResponse>('create-portal-session', {
    ...body,
  });
}

async function syncMySubscriptionFromStripe(): Promise<void> {
  await invokeFunctionWithAuth<unknown>('sync-my-subscription', {});
}

async function syncMySubscriptionFromPayPal(subscriptionId?: string): Promise<void> {
  await invokeFunctionWithAuth<unknown>('sync-paypal-subscription', subscriptionId ? { subscriptionId } : {});
}

interface NativeSubscriptionSyncResponse {
  success: boolean;
  provider: 'native_store';
  is_active: boolean;
  status: string | null;
  tier: string | null;
  billing_tier: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  management_url: string | null;
  store: string | null;
  product_identifier: string | null;
  preserved_stripe_subscription?: boolean;
  preserved_paypal_subscription?: boolean;
  has_direct_native_access?: boolean;
  has_unmapped_active_subscription?: boolean;
  subscription_override?: boolean;
  lifetime_member?: boolean;
  fallback?: boolean;
  degraded_reason?: string;
}

interface ExistingMembershipRecoveryResult {
  recovered: boolean;
  provider: 'stripe' | 'paypal' | 'native_store' | null;
}

async function syncMySubscriptionFromNativeStore(): Promise<NativeSubscriptionSyncResponse | null> {
  const data = await invokeFunctionWithAuth<NativeSubscriptionSyncResponse>('sync-native-subscription', {});
  return data || null;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function nativeSyncShowsAccess(result: NativeSubscriptionSyncResponse | null): boolean {
  if (!result) return false;
  return (
    result.is_active ||
    result.status === 'active' ||
    result.status === 'trialing' ||
    result.tier === 'premium' ||
    result.tier === 'trialing'
  );
}

function getWebBillingProvider(
  record: SubscriptionRecord | null | undefined,
): 'stripe' | 'paypal' | null {
  const provider = record?.billing_provider?.toLowerCase();
  if (provider === 'paypal' || record?.paypal_subscription_id) return 'paypal';
  if (provider === 'stripe' || record?.stripe_customer_id) return 'stripe';
  return null;
}

function subscriptionRecordShowsAccess(
  record: SubscriptionRecord | null | undefined,
): boolean {
  if (!record) return false;
  const status = record.status?.toLowerCase();
  return status === 'active' || status === 'trialing';
}

function nativeSyncShowsRecoverableAccess(
  result: NativeSubscriptionSyncResponse | null,
  webRecord: SubscriptionRecord | null | undefined,
): boolean {
  if (!nativeSyncShowsAccess(result)) return false;

  const webProvider = getWebBillingProvider(webRecord);
  const webStatus = webRecord?.status?.trim().toLowerCase() || null;
  const hasExplicitInactiveWebStatus = Boolean(webProvider && webStatus) &&
    !subscriptionRecordShowsAccess(webRecord);

  if (!hasExplicitInactiveWebStatus) return true;

  // A delinquent or canceled web record cannot be revived by a degraded
  // server fallback. Independent native-store evidence and deliberate grants
  // remain valid access sources.
  return Boolean(
    result?.has_direct_native_access ||
      result?.store ||
      result?.product_identifier ||
      result?.subscription_override ||
      result?.lifetime_member,
  );
}

function buildNativeBillingConversionEventId(args: {
  eventName: 'trial_started' | 'subscription_purchase';
  tier: BillingTier;
  productIdentifier: string | null;
  currentPeriodEnd: string | null;
}): string {
  return [
    args.eventName,
    'native_store',
    args.productIdentifier || `premium_${args.tier}`,
    args.currentPeriodEnd || 'no_period_end',
  ].join(':');
}

/** Price of the dedicated native paid-first / rescue annual product. */
const NATIVE_ANNUAL_4999_PRICE = 49.99;

async function trackNativeBillingConversionForAppsFlyer(args: {
  customerInfo: unknown;
  tier: BillingTier;
  userId: string;
  trackingContext: CheckoutTrackingContext;
  verifiedPrice?: { value: number; currency: string };
  /** VIP/Pro only: the store product the checkout bought (vipProCheckoutTracking). */
  storeProductId?: string | null;
}): Promise<void> {
  if (!IS_NATIVE_RUNTIME) return;

  const snapshot = extractNativeSubscriptionSnapshot(args.customerInfo);
  if (!snapshot.isActive) return;

  const billingTier = snapshot.billingTier || args.tier;
  const isOnboardingYearlyOffer =
    args.trackingContext.offerKind === 'onboarding_yearly';
  const isCurrentAnnualOffer =
    args.trackingContext.offerKind === 'annual_monthly_paid_annual' ||
    args.trackingContext.offerKind === 'paid_first_annual' ||
    args.trackingContext.offerKind === 'annual_monthly_annual' ||
    args.trackingContext.offerKind === 'annual_monthly_annual_trial' ||
    args.trackingContext.offerKind === 'annual_rescue';
  // These native offers sell the dedicated $49.99 store product.
  const nativeStoreAnnual4999Offer =
    isCurrentAnnualOffer && args.trackingContext.offerKind !== 'annual_monthly_paid_annual';
  // RevenueCat can report a trial as an INTRO period (status 'active'). A trial
  // offer kind (same list as the checkout isTrialOffer) marks a trial start only
  // when the new entitlement's period confirms it: intro-ineligible accounts
  // (e.g. annual-led base plan) are charged right away and stay purchases.
  const entitlementPeriodType = getEntitlementPeriodType(args.customerInfo);
  const isTrialing =
    snapshot.status === 'trialing' ||
    snapshot.tier === 'trialing' ||
    (isTrialOfferKind(args.trackingContext.offerKind) &&
      isEvidencedTrialPeriod(entitlementPeriodType));
  // Annual-led sells exactly one trial length by construction (the purchase
  // path verifies the 7-day product/option before charging), so the offer
  // kind is the trial-day truth there; legacy bridge kinds keep their
  // compiled lengths.
  const trialDays = isTrialing
    ? args.trackingContext.offerKind === 'annual_led_annual' ||
      args.trackingContext.offerKind === 'annual_monthly_annual_trial'
      ? ANNUAL_LED_TRIAL_DAYS
      : args.trackingContext.offerKind === 'paid_first_rescue_trial' ||
          args.trackingContext.offerKind === 'vip_rescue_annual_trial'
        ? PAID_FIRST_RESCUE_TRIAL_DAYS
      : billingTier === 'annual'
        ? 14
        : 7
    : 0;
  const eventName = isTrialing ? 'trial_started' : 'subscription_purchase';
  // A trial offer with no periodType cannot be labelled a trial, so the
  // purchase event carries an explicit null period and a debug marker.
  const trialOfferMissingPeriodType =
    !isTrialing &&
    isTrialOfferKind(args.trackingContext.offerKind) &&
    entitlementPeriodType == null;
  const periodKey = snapshot.currentPeriodEnd || 'no_period_end';
  const eventId = buildNativeBillingConversionEventId({
    eventName,
    tier: billingTier,
    productIdentifier: snapshot.productIdentifier,
    currentPeriodEnd: snapshot.currentPeriodEnd,
  });
  const purchaseKey = nativePurchaseDedupeKey({
    userId: args.userId,
    billingTier,
    periodKey,
  });
  const namedKey = nativeNamedCheckoutDedupeKey({
    userId: args.userId,
    eventName,
    billingTier,
    periodKey,
  });
  // PremiumWelcome can mark the purchase key first when server sync reports
  // access before this hook runs. A neighbour second counts: the SDK may
  // round while the server keeps milliseconds.
  const purchaseCandidates = nativePurchaseDedupeCandidates({
    userId: args.userId,
    billingTier,
    periodKey,
  });
  if (purchaseCandidates?.some((key) => isClientConversionTracked(key))) return;
  if (purchaseKey && !markClientConversionTracked(purchaseKey)) return;
  if (namedKey) markClientConversionTracked(namedKey);
  if (!markClientConversionTracked(eventId)) return;

  const productId =
    snapshot.productIdentifier ||
    `premium_${billingTier}${isTrialing ? '_trial' : ''}`;
  const productName = `Journal Party Premium ${PRICING_TIERS[billingTier].name}${isTrialing ? ' Trial' : ''}`;
  // The onboarding yearly offer bills the intro price in year one, so report
  // that instead of the standard tier price (USD estimate; the RevenueCat
  // webhook remains the source of truth for exact revenue). VIP and Pro
  // without a quote fall back to their own list price, never Premium's.
  const vipProFallbackPrice = vipProFallbackValue(args.trackingContext.offerKind, billingTier);
  const price = isTrialing
    ? 0
    : args.verifiedPrice
      ? args.verifiedPrice.value
    : vipProFallbackPrice !== null
      ? vipProFallbackPrice
    : isOnboardingYearlyOffer
      ? ONBOARDING_YEARLY_OFFER_PRICE
      : isCurrentAnnualOffer
        ? nativeStoreAnnual4999Offer
          ? NATIVE_ANNUAL_4999_PRICE
          : PRICING_TIERS.annual.price
        : billingTier === 'annual'
          ? LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE
          : PRICING_TIERS[billingTier].price;

  await trackEvent(
    eventName,
    {
      ...getNativeTrackingContext(),
      source: args.trackingContext.source || 'unknown',
      checkout_entry_source: args.trackingContext.source || 'unknown',
      paywall_type: args.trackingContext.paywallType || null,
      paywall_feature: args.trackingContext.paywallFeature || null,
      content_id: args.trackingContext.contentId || null,
      content_title: args.trackingContext.contentTitle || null,
      offer_kind: args.trackingContext.offerKind || null,
      offer_config: args.trackingContext.offerConfig || null,
      install_session: getInstallSessionIdForAttribution() || null,
      ...buildVipProCheckoutDetails({
        offerKind: args.trackingContext.offerKind,
        interval: billingTier,
        offerVersion: args.trackingContext.offerVersion,
        entryRoute: args.trackingContext.entryRoute,
        quotedPrice: args.trackingContext.quotedPrice,
        storeProductId: args.storeProductId ?? null,
      }),
      checkout_surface: 'native_revenuecat_purchase',
      provider: 'native_store',
      tier: billingTier,
      plan: billingTier,
      is_trialing: isTrialing,
      trial_days: trialDays,
      subscription_status: snapshot.status || null,
      entitlement_period_type: trialOfferMissingPeriodType ? null : entitlementPeriodType,
      ...(trialOfferMissingPeriodType ? { trial_offer_missing_period_type: true } : {}),
      current_period_end: snapshot.currentPeriodEnd || null,
      store: resolvePurchaseStore(snapshot.store),
      product_identifier: snapshot.productIdentifier || null,
      value: price,
      currency: args.verifiedPrice?.currency ?? 'USD',
      transaction_id: eventId,
      items: [
        {
          item_id: productId,
          item_name: productName,
          price,
          quantity: 1,
        },
      ],
    },
    {
      clientOnly: true,
      eventId,
      userId: args.userId,
      ...nativeConversionTracking(eventName),
    }
  );
}

async function syncMySubscriptionFromNativeStoreWithRetry(
  attempts = 5
): Promise<NativeSubscriptionSyncResponse | null> {
  let latest: NativeSubscriptionSyncResponse | null = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    latest = await syncMySubscriptionFromNativeStore();
    if (nativeSyncShowsAccess(latest)) return latest;

    if (attempt < attempts - 1) {
      await wait(Math.min(1500 * (attempt + 1), 5000));
    }
  }

  return latest;
}

function hasFutureScheduledPlanChange(
  record: Pick<SubscriptionRecord, 'subscription_type' | 'scheduled_subscription_type' | 'scheduled_change_at'> | null | undefined
): boolean {
  if (!record) return false;

  const currentTier = normalizeBillingTier(record.subscription_type);
  const scheduledTier = normalizeBillingTier(record.scheduled_subscription_type);
  if (!scheduledTier || scheduledTier === currentTier) return false;

  const scheduledTimestamp = record.scheduled_change_at ? Date.parse(record.scheduled_change_at) : NaN;
  return Number.isFinite(scheduledTimestamp) && scheduledTimestamp > Date.now();
}

async function fetchSubscriptionRecord(userId: string): Promise<SubscriptionRecord | null> {
  const { data, error } = await supabase
    .from('user_subscriptions_v2')
    .select('billing_provider, status, current_period_end, cancel_at_period_end, stripe_customer_id, paypal_subscription_id, paypal_manage_url, subscription_type, scheduled_subscription_type, scheduled_change_at, plan_tier')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching subscription record:', error);
    return null;
  }

  return data;
}

/**
 * Fetch billing history (invoices)
 */
async function fetchBillingHistory(): Promise<Invoice[]> {
  try {
    const data = await invokeFunctionWithAuth<{ invoices?: Invoice[] }>(
      'get-billing-history',
      {},
      { signOutOnInvalidSession: false }
    );
    return data?.invoices || [];
  } catch {
    return [];
  }
}

/**
 * Hook to manage subscription
 */
export function useSubscription() {
  const { user, session, profile, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutMethodLoading, setCheckoutMethodLoading] = useState<'card' | 'native_store' | null>(null);
  const [isRestorePurchasesLoading, setIsRestorePurchasesLoading] = useState(false);
  const lastPayPalSyncAtRef = useRef(0);
  const nativeBillingEnabled = isNativeBillingEnabled();
  const nativePurchaseFlowEnabled = isNativePurchaseFlowEnabled();
  const forceNativeBillingLoading = nativeBillingEnabled && isNativeBillingLoadingQaForced();

  const {
    data: subscriptionRecord,
    refetch: refetchSubscriptionRecord,
    isLoading: isLoadingSubscriptionRecord,
  } = useQuery({
    queryKey: ['subscriptionRecord', user?.id],
    queryFn: () => fetchSubscriptionRecord(user!.id),
    enabled: !!user?.id && !!session?.access_token,
    staleTime: 30 * 1000, // 30 seconds
  });

  const {
    data: nativeBillingState,
    refetch: refetchNativeBillingState,
    isLoading: isLoadingNativeBillingState,
  } = useQuery({
    queryKey: ['nativeBillingState', user?.id],
    enabled: !!user?.id && !!session?.access_token && nativeBillingEnabled,
    // PremiumWelcome calls refreshSubscriptionState on a 3s / 30s schedule
    // while a native trial offer is waiting on periodType. That calls
    // refetch(), which ignores staleTime. No standing interval: a native
    // member whose snapshot has no periodType is a normal state outside checkout.
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let snapshot: NativeSubscriptionSnapshot = extractNativeSubscriptionSnapshot(null);
      try {
        const customerInfo = await getNativeCustomerInfo(user!.id);
        snapshot = extractNativeSubscriptionSnapshot(customerInfo);
      } catch (error) {
        console.error('Failed to load native customer info:', error);
      }

      let synced: NativeSubscriptionSyncResponse | null = null;
      try {
        synced = await syncMySubscriptionFromNativeStore();
      } catch (error) {
        console.error('Failed to sync native subscription state:', error);
      }

      return { snapshot, synced };
    },
  });

  const nativeSnapshot = nativeBillingState?.snapshot ?? extractNativeSubscriptionSnapshot(null);
  const nativeSynced = nativeBillingState?.synced ?? null;
  const {
    subscriptionInfo,
    isPremium,
    isLifetime,
    isTrialing,
    hasBillingAccess,
    hasPremiumAccessResolved,
    isLegacyWebSubscriber,
  } = deriveSubscriptionState({
    subscriptionRecord,
    nativeSnapshot,
    nativeSynced,
    profileSubscriptionTier: profile?.subscription_tier,
    nativeBillingEnabled,
    getNativeStoreManagementUrlFallback,
    paypalManageUrlFallback: PAYPAL_MANAGE_URL_FALLBACK,
  });
  // Also treat subscription as loading when the query is disabled because
  // the session token hasn't arrived yet — prevents the route guard from
  // seeing "not loading + no premium" and redirecting prematurely.
  const subscriptionQueryNotYetEnabled =
    Boolean(user?.id) && !session?.access_token;
  const profileQueryNotYetResolved = Boolean(user?.id) && !profile;
  const isLoadingSubscription =
    Boolean(user?.id) &&
    (isLoadingSubscriptionRecord ||
      profileQueryNotYetResolved ||
      subscriptionQueryNotYetEnabled ||
      (nativeBillingEnabled && (isLoadingNativeBillingState || forceNativeBillingLoading)));

  useEffect(() => {
    if (!nativeBillingEnabled || !user?.id || !nativeSynced) return;
    const syncedTier = normalizeSubscriptionTier(nativeSynced.tier);
    const currentProfileTier = normalizeSubscriptionTier(profile?.subscription_tier);
    if (syncedTier && currentProfileTier !== syncedTier) {
      void refreshProfile();
    }
  }, [
    nativeBillingEnabled,
    user?.id,
    nativeSynced,
    profile?.subscription_tier,
    refreshProfile,
  ]);

  // Fetch billing history
  const {
    data: invoices = [],
    isLoading: isLoadingInvoices,
    error: billingHistoryError,
    refetch: refetchBillingHistory,
  } = useQuery({
    queryKey: ['billingHistory', user?.id],
    queryFn: fetchBillingHistory,
    enabled:
      !!user?.id &&
      !!session?.access_token &&
      hasBillingAccess &&
      !isLifetime &&
      subscriptionInfo.provider === 'stripe',
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Start checkout
  const startCheckout = useCallback(
    async (
      tier: BillingTier = 'monthly',
      returnPath?: string,
      trackingContext: CheckoutTrackingContext = {},
      handoffOptions: CheckoutHandoffOptions = {}
    ) => {
      if (!user) {
        throw new Error('Must be logged in to checkout');
      }
      // A checkout that should return to a Journey entry page (returnTo of the
      // form /journey/:id/start) lands there after payment. The entry page and
      // the server re-check access on arrival, so this is navigation only.
      const directJourneyId = resolveJourneyEntryTarget(returnPath);
      if (directJourneyId) trackingContext = { ...trackingContext, contentId: directJourneyId, contentType: 'journey' };

      const canStartNativeCheckoutWhileSyncing =
        nativeBillingEnabled && nativePurchaseFlowEnabled;

      if (isLoadingSubscription && !canStartNativeCheckoutWhileSyncing) {
        throw new Error('Still checking your billing status. Please try again in a moment.');
      }

      // Native save-offer rescue: the ONLY case where an active app-store
      // subscriber may start a checkout, because the checkout is an explicit
      // plan switch to the permanent $49.99 annual rate, not a second account.
      const isAnnualRescue = trackingContext.offerKind === 'annual_rescue';
      const annualRescueAllowed =
        isAnnualRescue &&
        IS_NATIVE_RUNTIME &&
        subscriptionInfo.provider === 'native_store' &&
        isNativeCancelOfferStatus(subscriptionInfo.status) &&
        tier === 'annual' &&
        Boolean(subscriptionInfo.productIdentifier) &&
        isNativeAnnualSaveOfferSourceProductId(
          subscriptionInfo.productIdentifier,
        );
      if (isAnnualRescue && !annualRescueAllowed) {
        throw new Error(
          'The yearly save offer is only available for an eligible active app store subscription.'
        );
      }

      // VIP/Pro Phase 5: a native Pro purchase may be a VIP member's upgrade
      // on the same store. nativeVipPro's cross-store guard decides that
      // before any store sheet opens, so the generic one-membership check
      // must not refuse it here.
      const isNativeProUpgrade =
        IS_NATIVE_RUNTIME && trackingContext.offerKind === 'pro_upgrade';
      const existingBillingMessage = annualRescueAllowed || isNativeProUpgrade
        ? null
        : getExistingBillingCheckoutMessage({
            hasBillingAccess,
            isLegacyWebSubscriber,
            provider: subscriptionInfo.provider,
            status: subscriptionInfo.status,
          });
      if (existingBillingMessage) {
        throw new Error(existingBillingMessage);
      }

      if (IS_NATIVE_RUNTIME && !nativePurchaseFlowEnabled) {
        throw new Error(
          'In-app subscriptions are disabled on this build. Existing App Store or Play Store subscribers can still restore purchases in Settings.'
        );
      }

      const loadingMethod: 'card' | 'native_store' =
        nativeBillingEnabled ? 'native_store' : 'card';
      setIsCheckoutLoading(true);
      setCheckoutMethodLoading(loadingMethod);

      // Native save-offer rescue: the redemption payload built from the
      // pre-purchase snapshot. Recorded (or parked for the sheet's retry)
      // on every path that ends in a completed switch, including the
      // "already owned" recovery below.
      let rescueRecordInput: RecordNativeSaveOfferInput | null = null;
      const recordOrParkRescue = async () => {
        if (!rescueRecordInput) return;
        const input = rescueRecordInput;
        rescueRecordInput = null;
        try {
          await recordNativeSaveOffer(input);
        } catch (recordError) {
          console.error('Failed to record the native save offer:', recordError);
          savePendingNativeRescueRecord(input);
        }
      };

      // Set only after this attempt writes the native offer, so an earlier
      // validation error does not wipe an offer the welcome page still needs.
      let persistedNativeOffer = false;

      try {
        if (IS_NATIVE_RUNTIME && !nativeBillingEnabled) {
          throw new Error('In-app billing is not configured on this mobile build.');
        }

        const isOnboardingYearlyOffer = trackingContext.offerKind === 'onboarding_yearly';
        const isAnnualLedAnnual =
          trackingContext.offerKind === 'annual_led_annual';
        const isAnnualLedWeekly =
          trackingContext.offerKind === 'annual_led_weekly';
        const isAnnualMonthlyPaidAnnual = trackingContext.offerKind === 'annual_monthly_paid_annual';
        const isAnnualMonthlyPaidMonthly = trackingContext.offerKind === 'annual_monthly_paid_monthly';
        const isAnnualMonthlyPaid = isAnnualMonthlyPaidAnnual || isAnnualMonthlyPaidMonthly;
        if ((isAnnualMonthlyPaidAnnual && tier !== 'annual') || (isAnnualMonthlyPaidMonthly && tier !== 'monthly')) {
          throw new Error('The paid membership offer does not match the selected plan.');
        }
        let verifiedPaidPrice: { value: number; currency: string } | undefined;
        const isVipProOffer =
          trackingContext.offerKind === 'vip_upfront' ||
          trackingContext.offerKind === 'vip_rescue_annual_trial' ||
          trackingContext.offerKind === 'pro_upgrade';
        if (isVipProOffer) {
          // The apps never sell through web checkout: natively these offer
          // kinds buy the App Store or Play products (nativeVipPro.ts).
          if (Capacitor.isNativePlatform() && !nativeBillingEnabled) {
            throw new Error('This plan is not available here.');
          }
          if (tier !== 'annual' && tier !== 'monthly') {
            throw new Error('Choose the monthly or annual plan.');
          }
          if (trackingContext.offerKind === 'vip_rescue_annual_trial' && tier !== 'annual') {
            throw new Error('The free trial is on the annual plan only.');
          }
          // The server quote the paywall showed; the checkout function checks
          // the real Stripe price against its catalog again. A trial starts
          // at $0, so the rescue reports no checkout value.
          if (trackingContext.offerKind !== 'vip_rescue_annual_trial') {
            verifiedPaidPrice = trackingContext.quotedPrice;
          }
        }
        if (isAnnualMonthlyPaid && nativeBillingEnabled) {
          const quote = await getNativeAnnualMonthlyPaidQuote(user.id);
          if (!quote) throw new Error('These membership plans are unavailable. Please try again.');
          verifiedPaidPrice = {
            value: tier === 'annual' ? quote.annualPrice : quote.monthlyPrice,
            currency: quote.currencyCode,
          };
          if (trackingContext.quotedPrice && (trackingContext.quotedPrice.value !== verifiedPaidPrice.value || trackingContext.quotedPrice.currency !== verifiedPaidPrice.currency)) {
            throw new Error('Your store price changed. Please reload the membership options and try again.');
          }
        }
        const isAnnualMonthlyAnnualTrial =
          trackingContext.offerKind === 'annual_monthly_annual_trial';
        const isAnnualMonthlyAnnual =
          trackingContext.offerKind === 'annual_monthly_annual';
        const isAnnualMonthlyMonthly =
          trackingContext.offerKind === 'annual_monthly_monthly';
        const isAnnualMonthlyOffer =
          isAnnualMonthlyAnnualTrial ||
          isAnnualMonthlyAnnual ||
          isAnnualMonthlyMonthly;
        // Paid-first uses dedicated verified no-trial store products. The
        // bridge's own trial-ineligible path stays on generic no_trial routing.
        const isPaidFirstAnnual =
          trackingContext.offerKind === 'paid_first_annual';
        const isPaidFirstWeekly =
          trackingContext.offerKind === 'paid_first_weekly';
        const isPaidFirstRescueTrial =
          trackingContext.offerKind === 'paid_first_rescue_trial';
        if (isPaidFirstAnnual && tier !== 'annual') {
          throw new Error('The paid-first annual offer requires the annual tier.');
        }
        if (isPaidFirstWeekly && tier !== 'weekly') {
          throw new Error('The paid-first weekly offer requires the weekly tier.');
        }
        if (isPaidFirstRescueTrial && tier !== 'annual') {
          throw new Error('The paid-first rescue trial requires the annual tier.');
        }
        if (isAnnualLedAnnual && tier !== 'annual') {
          throw new Error('The annual-led offer requires the annual tier.');
        }
        if (isAnnualLedWeekly && tier !== 'weekly') {
          throw new Error('The annual-led weekly anchor requires the weekly tier.');
        }
        if (
          (isAnnualMonthlyAnnualTrial || isAnnualMonthlyAnnual) &&
          tier !== 'annual'
        ) {
          throw new Error('The annual trial offer requires the annual tier.');
        }
        if (isAnnualMonthlyMonthly && tier !== 'monthly') {
          throw new Error('The monthly immediate-charge offer requires the monthly tier.');
        }
        if (isOnboardingYearlyOffer && tier !== 'annual') {
          throw new Error('The onboarding yearly offer requires the annual tier.');
        }
        if (trackingContext.offerKind === 'annual_trial' && tier !== 'annual') {
          throw new Error('The annual trial requires the annual tier.');
        }
        if (trackingContext.offerKind === 'bridge_annual_trial' && tier !== 'annual') {
          throw new Error('The annual trial requires the annual tier.');
        }
        if (trackingContext.offerKind === 'bridge_annual_paid' && tier !== 'annual') {
          throw new Error('The annual plan requires the annual tier.');
        }
        if (trackingContext.offerKind === 'bridge_monthly_trial' && tier !== 'monthly') {
          throw new Error('The monthly trial requires the monthly tier.');
        }
        if (
          trackingContext.offerKind === 'monthly_trial' &&
          tier !== 'monthly'
        ) {
          throw new Error('The monthly trial requires the monthly tier.');
        }
        const isTrialOffer =
          trackingContext.offerKind === 'annual_trial' ||
          trackingContext.offerKind === 'monthly_trial' ||
          trackingContext.offerKind === 'bridge_annual_trial' ||
          trackingContext.offerKind === 'bridge_monthly_trial' ||
          isAnnualLedAnnual ||
          isAnnualMonthlyAnnualTrial ||
          isPaidFirstRescueTrial ||
          trackingContext.offerKind === 'vip_rescue_annual_trial';
        // VIP and Pro without a quote report their own list price.
        const vipProCheckoutFallback = vipProFallbackValue(trackingContext.offerKind, tier);
        const checkoutValue = verifiedPaidPrice
          ? verifiedPaidPrice.value
          : isTrialOffer
          ? 0
          : vipProCheckoutFallback !== null
            ? vipProCheckoutFallback
          : isAnnualRescue
            ? 49.99
            : isOnboardingYearlyOffer
              ? ONBOARDING_YEARLY_OFFER_PRICE
            : isPaidFirstAnnual || isAnnualMonthlyAnnual
              ? NATIVE_ANNUAL_4999_PRICE
              : isAnnualMonthlyPaidAnnual
              ? PRICING_TIERS.annual.price
              : nativeBillingEnabled && tier === 'annual'
                ? LEGACY_NATIVE_BRIDGE_ANNUAL_PRICE
                : PRICING_TIERS[tier].price;

        if (nativeBillingEnabled) {
          // VIP/Pro Phase 6: the RevenueCat webhook recovers the offer
          // details from this row, so it names the store product too. Legacy
          // offers skip the lookup and keep their exact payload.
          const vipProStoreProductId = isVipProOffer
            ? await resolveNativeVipProStoreProductId(trackingContext.offerKind, tier)
            : undefined;
          void trackEvent('checkout_started', {
            ...getNativeTrackingContext(),
            tier,
            source: trackingContext.source || 'unknown',
            paywall_type: trackingContext.paywallType || null,
            paywall_feature: trackingContext.paywallFeature || null,
            content_id: trackingContext.contentId || null,
            content_title: trackingContext.contentTitle || null,
            offer_kind: trackingContext.offerKind || null,
            offer_config: trackingContext.offerConfig || null,
            install_session: getInstallSessionIdForAttribution() || null,
            provider: 'native_store',
            value: checkoutValue,
            currency: verifiedPaidPrice?.currency ?? 'USD',
            ...buildVipProCheckoutDetails({
              offerKind: trackingContext.offerKind,
              interval: tier,
              offerVersion: trackingContext.offerVersion,
              entryRoute: trackingContext.entryRoute,
              quotedPrice: trackingContext.quotedPrice,
              storeProductId: vipProStoreProductId,
            }),
          }, isAnnualRescue || (isNativeProUpgrade && hasBillingAccess)
            ? {
                // An existing subscriber switching plans is not an acquisition
                // signal. Skip AppsFlyer too because its partner mappings would
                // otherwise forward this checkout to Meta and TikTok.
                skipPlatforms: ['meta', 'tiktok', 'appsflyer'],
              }
            : {
                // AppsFlyer is the single native checkout owner and forwards
                // af_initiated_checkout to both Meta and TikTok. Keep the
                // first-party server record, but suppress direct Pixel/CAPI
                // paths so the partner events are not counted twice.
                skipPlatforms: ['meta', 'tiktok'],
              });

          // Persist the offer before StoreKit / Play opens. When the purchase
          // result is not active yet, the hook sends nothing and the welcome
          // page sends the one fallback conversion; it needs the offer kind to
          // read a later INTRO period as a trial start, not a purchase.
          storeCheckoutAttributionContext({
            source: trackingContext.source || 'unknown',
            sourcePath: returnPath || null,
            paywallType: trackingContext.paywallType || null,
            paywallFeature: trackingContext.paywallFeature || null,
            contentId: trackingContext.contentId || null,
            contentTitle: trackingContext.contentTitle || null,
            returnPath: returnPath || null,
            provider: 'native_store',
            tier,
            offerKind: trackingContext.offerKind ?? null,
          });
          persistedNativeOffer = true;

          // Start RevenueCat enrichment before opening StoreKit / Play, but do
          // not await network flushing here. Purchase confirmation can take
          // long enough for the attributes to arrive, and the webhook also has
          // the first-party checkout event as a fallback. Measurement must
          // never delay or block the native purchase sheet.
          void syncNativeCheckoutAttribution(user.id, {
            offerKind: trackingContext.offerKind,
            paywallFeature: trackingContext.paywallFeature,
            offerConfig: trackingContext.offerConfig,
            installSession: getInstallSessionIdForAttribution(),
          }).catch((error) => {
            // Measurement enrichment must never prevent StoreKit or Play
            // Billing from presenting the purchase confirmation.
            console.error('Failed to sync native checkout attribution:', error);
          });

          // Rescue: capture the subscriber's CURRENT product and store before
          // the switch. This cannot be reconstructed reliably after StoreKit
          // reports the new annual product.
          const rescueSnapshot = isAnnualRescue
            ? {
                store: canonicalNativeStore(),
                previousProductId: subscriptionInfo.productIdentifier as string,
              }
            : null;
          if (
            rescueSnapshot &&
            rescueSnapshot.store !== 'app_store' &&
            rescueSnapshot.store !== 'play_store'
          ) {
            throw new Error('The yearly save offer is not available in this app store.');
          }
          if (rescueSnapshot?.store) {
            rescueRecordInput = {
              store: rescueSnapshot.store,
              previousProductId: rescueSnapshot.previousProductId,
              nativeProductId: getNativeAnnualSaveOfferProductId(),
              pitchId: trackingContext.rescue?.pitchId ?? null,
              cancelReason: trackingContext.rescue?.cancelReason ?? null,
            };
          }

          // Annual-led reports the store's own price for a paid conversion.
          // iOS sells jp_premium_annual_trial7 at $49.99, while Play reuses
          // the legacy annual base plan, so no single constant is right.
          let annualLedChargedPrice: { value: number; currency: string } | undefined;
          const purchaseAnnualLedAnnual = async () => {
            const purchase = await purchaseAnnualLedPlanWithPrice(user.id, 'annual');
            annualLedChargedPrice = purchase.chargedPrice ?? undefined;
            return purchase.customerInfo;
          };
          // VIP/Pro: the store quote the member saw must still be the price.
          // The guard inside the purchase says whether it replaced a VIP plan.
          let replacedVipPlan = false;
          const purchaseVipPro = async () => {
            const { purchaseNativeVipProWithChange, purchaseNativeVipRescue } = await import('@/services/nativeVipPro');
            const expected = trackingContext.quotedPrice
              ? { price: trackingContext.quotedPrice.value, currencyCode: trackingContext.quotedPrice.currency }
              : undefined;
            if (trackingContext.offerKind === 'vip_rescue_annual_trial') {
              return purchaseNativeVipRescue(user.id, expected);
            }
            const purchase = await purchaseNativeVipProWithChange(user.id, {
              tier: trackingContext.offerKind === 'pro_upgrade' ? 'pro' : 'vip',
              interval: tier === 'annual' ? 'annual' : 'monthly',
              expected,
            });
            replacedVipPlan = purchase.changeFrom === 'vip';
            return purchase.customerInfo;
          };
          const customerInfo = isVipProOffer
            ? await purchaseVipPro()
            : isAnnualRescue && rescueSnapshot
            ? await purchaseNativeAnnualSaveOffer(
                user.id,
                rescueSnapshot.previousProductId,
              )
            : isPaidFirstRescueTrial
              ? await purchaseNativePaidFirstRescueTrial(user.id)
            : isAnnualMonthlyPaid
              ? await purchaseNativeAnnualMonthlyPaidPlan(user.id, tier === 'annual' ? 'annual' : 'monthly', verifiedPaidPrice)
            : isAnnualMonthlyOffer
              ? await purchaseNativeAnnualTrialMonthlyPlan(
                  user.id,
                  tier === 'annual' ? 'annual' : 'monthly',
                )
            : isAnnualLedAnnual
            ? await purchaseAnnualLedAnnual()
            : isAnnualLedWeekly
              ? await purchaseAnnualLedPlan(user.id, 'weekly')
              : isOnboardingYearlyOffer
                ? await purchaseNativeOnboardingYearlyOffer(user.id)
                : isTrialOffer
                  ? await purchaseNativeSubscription(user.id, tier)
                  : isPaidFirstAnnual || isPaidFirstWeekly
                    ? await purchaseNativeSubscription(user.id, tier, {
                        googleOfferMode: 'base_plan',
                        nativeOfferMode: 'paid_first_no_trial',
                        iosOfferMode: 'paid_first_no_trial',
                      })
                    : tier === 'monthly'
                      ? await purchaseNativeSubscription(user.id, tier, {
                          // Google needs the paid base plan selected explicitly.
                          // On iOS the standard monthly product serves both
                          // eligible and ineligible accounts; StoreKit decides
                          // whether its introductory offer applies.
                          googleOfferMode: 'base_plan',
                        })
                      : await purchaseNativeSubscription(user.id, tier, {
                          googleOfferMode: 'base_plan',
                          iosOfferMode: 'no_trial',
                        });
          // A VIP member's move to Pro is a plan change, not a first sale
          // (plan section 7); the webhook records it.
          if (!replacedVipPlan) {
            await trackNativeBillingConversionForAppsFlyer({
              customerInfo,
              tier,
              userId: user.id,
              trackingContext,
              verifiedPrice: verifiedPaidPrice ?? annualLedChargedPrice,
              ...(isVipProOffer ? { storeProductId: vipProStoreProductId } : {}),
            });
          }
          // The hook owns the conversion when the store snapshot is active, so
          // the saved offer must not label a later welcome-page fallback.
          // The checkout tier stays: an unmapped product id has no snapshot
          // tier, and the page would otherwise fall back to monthly and send
          // a second event. An inactive snapshot keeps the full offer.
          if (extractNativeSubscriptionSnapshot(customerInfo).isActive) {
            clearCheckoutAttributionContext();
            storeCheckoutAttributionContext({
              provider: 'native_store',
              tier,
            });
            persistedNativeOffer = false;
          }
          // Record the redemption now, with the pre-purchase snapshot. A
          // failure never blocks the customer: park it and let the rescue
          // sheet retry once after the reload; the RevenueCat webhook
          // confirms the switch server-side either way.
          await recordOrParkRescue();
          // The purchase result already contains RevenueCat's signed active
          // entitlement. Reconcile the production record in the background,
          // but never hold the customer on the paywall for that server round
          // trip. TestFlight sandbox subscriptions are intentionally excluded
          // from production records, and production webhooks remain the
          // authoritative backstop for live purchases.
          void syncMySubscriptionFromNativeStore().catch((error) => {
            console.error('Native sync after purchase failed:', error);
          });

          await recordNativePurchaseConfirmed(handoffOptions);

          // A canceled or failed StoreKit/Play purchase never reaches this
          // point. Require the onboarding decision write before reporting
          // navigation success.
          // If setup is temporarily unavailable, the paid state remains local
          // and the paywall can offer a safe retry without charging again.
          await requireNativeCheckoutHandoff(handoffOptions);

          setIsCheckoutLoading(false);
          setCheckoutMethodLoading(null);
          const postCheckoutPath = withCheckoutResult(
            resolveNativePostCheckoutPath(returnPath),
            'success'
          );
          window.location.href = postCheckoutPath;
          return;
        }

        const normalizedReturnPath = returnPath || '/pricing';

        const checkoutProvider = 'stripe';
        const isOnboardingCheckout = isOnboardingHostedCheckout(trackingContext);
        const onboardingCheckoutUrls = isOnboardingCheckout
          ? buildOnboardingHostedCheckoutUrls(checkoutProvider)
          : null;
        const journeyCheckoutUrls = directJourneyId ? buildJourneyCheckoutUrls(directJourneyId, checkoutProvider, { day: getJourneyEntryDay(returnPath || '') }) : null;
        // A program or Journey chosen with Start (the start intent) returns
        // there after payment. An explicit Journey or onboarding checkout
        // keeps its own return.
        const startReturnPath =
          journeyCheckoutUrls || onboardingCheckoutUrls
            ? null
            : resolveStartIntentCheckoutReturn(returnPath);
        const successUrl =
          journeyCheckoutUrls?.successUrl ??
          onboardingCheckoutUrls?.successUrl ??
          buildExternalWebUrl('/welcome-premium', {
            checkout: 'success',
            ...(startReturnPath ? { returnTo: startReturnPath } : {}),
          });
        const cancelUrl =
          journeyCheckoutUrls?.cancelUrl ??
          onboardingCheckoutUrls?.cancelUrl ??
          buildExternalWebUrl(normalizedReturnPath, { checkout: 'canceled' });

        storeCheckoutAttributionContext({
          source: trackingContext.source || 'unknown',
          sourcePath: normalizedReturnPath,
          paywallType: trackingContext.paywallType || null,
          paywallFeature: trackingContext.paywallFeature || null,
          contentId: trackingContext.contentId || null,
          contentTitle: trackingContext.contentTitle || null,
          returnPath: normalizedReturnPath,
          provider: checkoutProvider,
          tier,
          // This write replaces any context the paywall stored before calling
          // startCheckout, so it must carry the offer or the hosted return
          // reports a $67 purchase at the standard price.
          offerKind: trackingContext.offerKind ?? null,
          // VIP/Pro: the quoted price travels with it for the same reason.
          quotedValue: isVipProOffer ? verifiedPaidPrice?.value ?? null : null,
          quotedCurrency: isVipProOffer ? verifiedPaidPrice?.currency ?? null : null,
        });

        void trackEvent('checkout_started', {
          tier,
          source: trackingContext.source || 'unknown',
          paywall_type: trackingContext.paywallType || null,
          paywall_feature: trackingContext.paywallFeature || null,
          content_id: trackingContext.contentId || null,
          content_title: trackingContext.contentTitle || null,
          offer_kind: trackingContext.offerKind || null,
          return_path: normalizedReturnPath,
          provider: checkoutProvider,
          value: checkoutValue,
          currency: 'USD',
          ...buildVipProCheckoutDetails({
            offerKind: trackingContext.offerKind,
            interval: tier,
            offerVersion: trackingContext.offerVersion,
            entryRoute: trackingContext.entryRoute,
            quotedPrice: trackingContext.quotedPrice,
          }),
        }, {
          // Opt-in to destination fanout so Meta/TikTok CAPI receive an
          // InitiateCheckout event even when browser pixels are blocked.
          // Matches the PricingCheckoutPanel / LiveFunnel pattern. Covers
          // any paywall that bypasses those panels and jumps straight to
          // the useSubscription Stripe checkout path.
          fanoutMode: 'destinations',
          fanoutTargets: ['meta', 'tiktok'],
        });

        const { url } = await createCheckoutSession(tier, successUrl, cancelUrl, trackingContext);
        if (handoffOptions.canRedirect?.() === false) return;

        if (!isOnboardingCheckout) {
          const handoffRecorded = await handoffOptions.onHandoffReady?.();
          if (handoffRecorded === false) {
            throw new Error('We could not finish setting up your account. Please try again.');
          }
        }

        // Redirect to Stripe Checkout
        window.location.href = url;
      } catch (error) {
        // The store purchase is already confirmed. This is an account-setup
        // retry, not a checkout failure, so never emit checkout_failed.
        if (isNativeCheckoutCompletionPendingError(error)) {
          setIsCheckoutLoading(false);
          setCheckoutMethodLoading(null);
          throw error;
        }

        if (nativeBillingEnabled && isNativePurchaseAlreadyOwnedError(error)) {
          // A rescue switch is not "already owned": the subscriber's current
          // plan is what the store reports as owned. Only the annual product
          // itself counts as a completed switch; anything else is a failure.
          if (trackingContext.offerKind === 'annual_rescue') {
            const recoveredNativeState = await refetchNativeBillingState().catch(() => null);
            const recoveredProduct =
              recoveredNativeState?.data?.snapshot.productIdentifier ?? null;
            setIsCheckoutLoading(false);
            setCheckoutMethodLoading(null);
            if (isNativeAnnualSaveOfferTargetProductId(recoveredProduct)) {
              // The store already applied the switch; the redemption still
              // has to be recorded (or parked) exactly like a fresh purchase.
              await recordOrParkRescue();
              await recordNativePurchaseConfirmed(handoffOptions);
              await requireNativeCheckoutHandoff(handoffOptions);
              window.location.href = withCheckoutResult(
                resolveNativePostCheckoutPath(returnPath),
                'success'
              );
              return;
            }
            throw new Error('The store could not complete the switch. Nothing changed.');
          }
          const recoveredSync = await syncMySubscriptionFromNativeStoreWithRetry().catch(
            (syncError) => {
              console.error('Native sync after already-owned purchase failed:', syncError);
              return null;
            }
          );
          await refreshProfile();
          const [, recoveredNativeState] = await Promise.all([
            refetchSubscriptionRecord(),
            refetchNativeBillingState(),
          ]);
          void invalidateCoreAccess(queryClient);
          const recoveredAccess =
            nativeSyncShowsAccess(recoveredSync) ||
            Boolean(recoveredNativeState.data?.snapshot.isActive) ||
            nativeSyncShowsAccess(recoveredNativeState.data?.synced ?? null);

          if (!recoveredAccess) {
            if (persistedNativeOffer) {
              clearCheckoutAttributionContext();
              persistedNativeOffer = false;
            }
            setIsCheckoutLoading(false);
            setCheckoutMethodLoading(null);
            throw new Error(
              'Your store account already owns this membership, but we could not refresh access yet. Check your connection and try again.'
            );
          }

          // RevenueCat/StoreKit can report already-owned while local state is
          // stale. Once access is recovered, this is the same successful
          // onboarding handoff as a new purchase and must record the decision
          // before leaving the offer.
          try {
            await recordNativePurchaseConfirmed(handoffOptions);
            await requireNativeCheckoutHandoff(handoffOptions);
          } catch (handoffError) {
            setIsCheckoutLoading(false);
            setCheckoutMethodLoading(null);
            throw handoffError;
          }

          setIsCheckoutLoading(false);
          setCheckoutMethodLoading(null);
          const postCheckoutPath = withCheckoutResult(
            resolveNativePostCheckoutPath(returnPath),
            'success'
          );
          window.location.href = postCheckoutPath;
          return;
        }

        if (nativeBillingEnabled && isNativePurchaseCanceledError(error)) {
          // No purchase happened, so the offer persisted above must not label
          // a later, unrelated native conversion.
          clearCheckoutAttributionContext();
          void trackEvent('checkout_canceled', {
            ...getNativeTrackingContext(),
            tier,
            source: trackingContext.source || 'unknown',
            paywall_type: trackingContext.paywallType || null,
            paywall_feature: trackingContext.paywallFeature || null,
            content_id: trackingContext.contentId || null,
            content_title: trackingContext.contentTitle || null,
            provider: 'native_store',
          });
          setIsCheckoutLoading(false);
          setCheckoutMethodLoading(null);
          throw new Error('Purchase canceled.');
        }

        console.error('Checkout error:', error);
        // A non-cancel store failure stored the offer above. Drop it so a later
        // welcome page does not treat that attempt as the current checkout.
        if (persistedNativeOffer) {
          clearCheckoutAttributionContext();
          persistedNativeOffer = false;
        }
        const failedProvider =
          nativeBillingEnabled
            ? 'native_store'
            : 'stripe';
        void trackEvent('checkout_failed', {
          ...getNativeTrackingContext(),
          tier,
          source: trackingContext.source || 'unknown',
          paywall_type: trackingContext.paywallType || null,
          paywall_feature: trackingContext.paywallFeature || null,
          content_id: trackingContext.contentId || null,
          content_title: trackingContext.contentTitle || null,
          provider: failedProvider,
          error_message: error instanceof Error ? error.message : 'unknown_error',
        });
        setIsCheckoutLoading(false);
        setCheckoutMethodLoading(null);
        throw error;
      }
    },
    [
      user,
      profile?.id,
      nativeBillingEnabled,
      nativePurchaseFlowEnabled,
      isLegacyWebSubscriber,
      isLoadingSubscription,
      subscriptionInfo.provider,
      hasBillingAccess,
      queryClient,
      refreshProfile,
      refetchSubscriptionRecord,
      refetchNativeBillingState,
    ]
  );

  const refreshSubscriptionState = useCallback(async () => {
    if (nativeBillingEnabled) {
      await syncMySubscriptionFromNativeStore().catch((error) => {
        console.error('Failed to sync native subscription state:', error);
      });
      await refreshProfile();
      const [result] = await Promise.all([
        refetchSubscriptionRecord(),
        refetchNativeBillingState(),
      ]);
      void invalidateCoreAccess(queryClient);
      return result.data ?? null;
    }

    if (subscriptionInfo.provider === 'paypal') {
      await syncMySubscriptionFromPayPal().catch((error) => {
        console.error('Failed to sync PayPal subscription state:', error);
      });
    } else {
      await syncMySubscriptionFromStripe();
    }
    await refreshProfile();
    const result = await refetchSubscriptionRecord();
    void invalidateCoreAccess(queryClient);

    await refetchBillingHistory();

    return result.data ?? null;
  }, [
    queryClient,
    nativeBillingEnabled,
    subscriptionInfo.provider,
    refreshProfile,
    refetchSubscriptionRecord,
    refetchBillingHistory,
    refetchNativeBillingState,
  ]);

  const recoverExistingMembership = useCallback(async (): Promise<ExistingMembershipRecoveryResult> => {
    if (!user) {
      throw new Error('Sign in before checking an existing membership.');
    }

    const initialResult = await refetchSubscriptionRecord();
    const initialRecord = initialResult.data ?? subscriptionRecord ?? null;
    const initialProvider = getWebBillingProvider(initialRecord);

    // PayPal needs the provider identity stored on the account row. Stripe can
    // also recover by authenticated email, so it is the safe fallback when the
    // local record has not been classified yet.
    if (initialProvider === 'paypal') {
      await syncMySubscriptionFromPayPal();
    } else {
      await syncMySubscriptionFromStripe();
    }

    let nativeSync: NativeSubscriptionSyncResponse | null = null;
    if (nativeBillingEnabled) {
      nativeSync = await syncMySubscriptionFromNativeStore();
    }

    await refreshProfile();
    const [latestResult, latestNativeResult] = await Promise.all([
      refetchSubscriptionRecord(),
      nativeBillingEnabled
        ? refetchNativeBillingState()
        : Promise.resolve({ data: null }),
    ]);
    void invalidateCoreAccess(queryClient);
    const latestRecord = latestResult.data ?? null;
    const latestProvider = getWebBillingProvider(latestRecord);
    const latestNativeSync = latestNativeResult.data?.synced ?? nativeSync;
    const recoveredFromWeb = subscriptionRecordShowsAccess(latestRecord);
    const recoveredFromNative = nativeSyncShowsRecoverableAccess(
      latestNativeSync,
      latestRecord,
    );
    const recovered = recoveredFromWeb || recoveredFromNative;

    return {
      recovered,
      provider:
        recoveredFromWeb
          ? latestProvider ?? initialProvider
          : recoveredFromNative
            ? 'native_store'
            : latestProvider ?? initialProvider ?? null,
    };
  }, [
    queryClient,
    user,
    subscriptionRecord,
    nativeBillingEnabled,
    refreshProfile,
    refetchSubscriptionRecord,
    refetchNativeBillingState,
  ]);

  useEffect(() => {
    if (!user?.id || !session?.access_token) return;
    if (subscriptionInfo.provider !== 'paypal') return;

    let isCancelled = false;

    const syncFromPayPalIfNeeded = async (force = false) => {
      const now = Date.now();
      if (!force && now - lastPayPalSyncAtRef.current < 15_000) {
        return;
      }

      lastPayPalSyncAtRef.current = now;

      try {
        await refreshSubscriptionState();
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to refresh PayPal subscription state:', error);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncFromPayPalIfNeeded();
      }
    };

    const handleFocus = () => {
      void syncFromPayPalIfNeeded();
    };

    void syncFromPayPalIfNeeded(true);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isCancelled = true;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, session?.access_token, subscriptionInfo.provider, refreshSubscriptionState]);

  const syncPayPalSubscription = useCallback(async (subscriptionId?: string) => {
    await syncMySubscriptionFromPayPal(subscriptionId);
    await refreshProfile();
    const result = await refetchSubscriptionRecord();
    void invalidateCoreAccess(queryClient);
    return result.data ?? null;
  }, [queryClient, refreshProfile, refetchSubscriptionRecord]);

  const restoreStorePurchases = useCallback(async () => {
    if (!user) {
      throw new Error('Must be logged in to restore purchases.');
    }
    if (!nativeBillingEnabled) {
      throw new Error('Restore purchases is only available in the mobile app.');
    }
    if (isLegacyWebSubscriber && hasBillingAccess) {
      throw new Error(
        'Your Premium access is from web checkout. No store restore is needed.'
      );
    }

    setIsRestorePurchasesLoading(true);
    try {
      const customerInfo = await restoreNativeSubscriptionPurchases(user.id);
      const entitlementId = getNativeEntitlementId();
      const activeEntitlements = customerInfo?.entitlements?.active ?? {};
      const hasConfiguredActiveEntitlement = Boolean(activeEntitlements[entitlementId]);
      const hasAnyActiveEntitlement = Object.keys(activeEntitlements).length > 0;
      const activeSubscriptions = customerInfo?.activeSubscriptions ?? [];
      const hasAnyActiveSubscriptionRecord = Boolean(activeSubscriptions.length);
      const hasKnownPremiumActiveSubscription = activeSubscriptions.some((productId) =>
        isKnownNativePremiumProductId(productId)
      );
      const hasAnyHistoricalSubscriptionRecord = Boolean(customerInfo?.allPurchasedProductIdentifiers?.length);

      if (!hasAnyActiveEntitlement && !hasAnyActiveSubscriptionRecord) {
        if (hasAnyHistoricalSubscriptionRecord) {
          throw new Error(
            'No active store subscription was found to restore. This account appears to have only expired or canceled purchases.'
          );
        }
        throw new Error('No store purchases were found to restore for this account.');
      }

      if (!hasConfiguredActiveEntitlement && hasAnyActiveSubscriptionRecord && !hasKnownPremiumActiveSubscription) {
        throw new Error(
          'A store purchase was found, but it is not mapped to your Premium entitlement yet. Please contact support and we will fix this quickly.'
        );
      }

      let synced: NativeSubscriptionSyncResponse | null = null;
      try {
        synced = await syncMySubscriptionFromNativeStore();
      } catch (error) {
        console.error('Native sync after restore failed:', error);
      }

      await refreshProfile();
      await Promise.all([refetchSubscriptionRecord(), refetchNativeBillingState()]);
      void invalidateCoreAccess(queryClient);

      if (
        synced &&
        !synced.is_active &&
        synced.tier === 'free' &&
        !synced.preserved_stripe_subscription &&
        !synced.preserved_paypal_subscription
      ) {
        if (synced.has_unmapped_active_subscription && hasKnownPremiumActiveSubscription) {
          return;
        }
        throw new Error(
          'Restore completed, but there is no active store subscription linked to this account yet.'
        );
      }
    } catch (error) {
      console.error('Restore purchases error:', error);
      throw error;
    } finally {
      setIsRestorePurchasesLoading(false);
    }
  }, [
    queryClient,
    user,
    nativeBillingEnabled,
    isLegacyWebSubscriber,
    hasBillingAccess,
    refreshProfile,
    refetchSubscriptionRecord,
    refetchNativeBillingState,
  ]);

  const openWebBillingSettings = useCallback(async () => {
    await openNativeSubscriptionManagementUrl(WEB_BILLING_SETTINGS_URL);
  }, []);

  // Open customer portal
  const openCustomerPortal = useCallback(async (targetTier?: BillingTier): Promise<PortalSessionResponse> => {
      if (!user || !hasBillingAccess || isLifetime) {
        throw new Error('No active billing subscription available to manage');
      }

      try {
        if (subscriptionInfo.provider === 'native_store') {
          const managementLink =
            subscriptionInfo.managementUrl || getNativeStoreManagementUrlFallback();
          if (!managementLink) {
            throw new Error('No store subscription management link available.');
          }

        await openNativeSubscriptionManagementUrl(managementLink);

        return {
          url: managementLink,
          message: targetTier
            ? 'Opened your store subscriptions page. Switch plans there.'
            : 'Opened your store subscriptions page.',
          };
        }

        if (subscriptionInfo.provider === 'none') {
          throw new Error('Billing changes are managed in the place where this subscription was originally purchased.');
        }

        if (subscriptionInfo.provider === 'paypal') {
          const managementLink = subscriptionInfo.managementUrl || PAYPAL_MANAGE_URL_FALLBACK;
          await openNativeSubscriptionManagementUrl(managementLink);

          return {
            url: managementLink,
            message: targetTier
              ? 'Opened PayPal billing settings. Switch plans there.'
              : 'Opened PayPal billing settings.',
          };
        }

        const returnUrl = new URL(buildExternalWebUrl('/settings'));
        returnUrl.searchParams.set('billing_return', '1');
        returnUrl.searchParams.set('billing_ts', String(Date.now()));
      if (targetTier) {
        returnUrl.searchParams.set('billing_target', targetTier);
      }

      const result = await createPortalSession(returnUrl.toString(), targetTier);

      if (targetTier && (result.switched || result.scheduled)) {
        if (result.switched) {
          await refreshSubscriptionState();
        }
        return result;
      }

      if (result.scheduled) {
        // Server-side scheduled changes (for example annual -> monthly) may not redirect.
        return result;
      }

      if (result.switched) {
        await refreshSubscriptionState();
        return result;
      }

      if (result.url) {
        window.location.href = result.url;
        return result;
      }

      throw new Error('Unable to open billing portal');
    } catch (error) {
      console.error('Portal error:', error);
      throw error;
    }
  }, [user, hasBillingAccess, isLifetime, refreshSubscriptionState, subscriptionInfo.provider, subscriptionInfo.managementUrl]);

  const cancelScheduledPlanChange = useCallback(async (): Promise<PortalSessionResponse> => {
    if (!user || !hasBillingAccess || isLifetime) {
      throw new Error('No active billing subscription available to manage');
    }

    if (subscriptionInfo.provider === 'native_store') {
      const result = await openCustomerPortal();
      return {
        ...result,
        message: 'Opened your store subscriptions page. Keep your current plan there.',
      };
    }

    if (subscriptionInfo.provider === 'paypal') {
      const result = await openCustomerPortal();
      return {
        ...result,
        message: 'Opened PayPal billing settings. Keep your current plan there.',
      };
    }

    const returnUrl = new URL(buildExternalWebUrl('/settings'));
    returnUrl.searchParams.set('billing_return', '1');
    returnUrl.searchParams.set('billing_ts', String(Date.now()));

    const result = await createPortalSession(
      returnUrl.toString(),
      undefined,
      'cancel_scheduled_change'
    );

    if (result.url && !result.switched && !result.scheduled) {
      throw new Error('Unable to cancel scheduled billing change right now. Please try again.');
    }

    const latest = await refreshSubscriptionState();
    if (hasFutureScheduledPlanChange(latest)) {
      throw new Error('Your plan change is still scheduled. Please refresh and try again.');
    }

    return result;
  }, [user, hasBillingAccess, isLifetime, refreshSubscriptionState, subscriptionInfo.provider, openCustomerPortal]);

  const cancelSubscriptionAtPeriodEnd = useCallback(async (): Promise<PortalSessionResponse> => {
    if (!user || !hasBillingAccess || isLifetime) {
      throw new Error('No active billing subscription available to manage');
    }

    if (subscriptionInfo.provider === 'native_store') {
      const result = await openCustomerPortal();
      return {
        ...result,
        message: 'Opened your store subscriptions page. Cancel auto-renewal there.',
      };
    }

    const returnUrl = new URL(buildExternalWebUrl('/settings'));
    returnUrl.searchParams.set('billing_return', '1');
    returnUrl.searchParams.set('billing_ts', String(Date.now()));

    const result = await createPortalSession(
      returnUrl.toString(),
      undefined,
      'cancel_subscription'
    );

    const latest = await refreshSubscriptionState();
    if (!latest?.cancel_at_period_end) {
      throw new Error('Unable to schedule cancellation right now. Please try again.');
    }

    return result;
  }, [user, hasBillingAccess, isLifetime, refreshSubscriptionState, subscriptionInfo.provider, openCustomerPortal]);

  const applySaveOffer = useCallback(
    async (offer: string, reason: string): Promise<SaveOfferResponse> => {
      if (!user) {
        throw new Error('Not signed in');
      }

      const result = await invokeFunctionWithAuth<SaveOfferResponse>('apply-save-offer', {
        offer,
        reason,
      });

      if (result?.applied) {
        await refreshSubscriptionState();
      }

      return result;
    },
    [user, refreshSubscriptionState]
  );

  const checkSaveOfferEligibility = useCallback(
    async (offer: string): Promise<SaveOfferResponse> => {
      if (!user) {
        throw new Error('Not signed in');
      }

      return invokeFunctionWithAuth<SaveOfferResponse>('apply-save-offer', {
        offer,
        checkOnly: true,
      });
    },
    [user]
  );

  // Check if feature requires premium
  const requiresPremium = useCallback(
    (feature: 'create_program' | 'premium_program') => {
      if (hasPremiumAccessResolved) return false;

      // All these features require premium for free users
      return true;
    },
    [hasPremiumAccessResolved]
  );

  return {
    // State
    subscriptionInfo,
    isPremium,
    hasPremiumAccess: hasPremiumAccessResolved,
    hasBillingAccess,
    isLifetime,
    isTrialing,
    currentBillingTier: subscriptionInfo.billingTier,
    memberPlanTier: memberPlanTierFor({
      productIdentifier: subscriptionInfo.productIdentifier,
      record: subscriptionRecord,
    }),
    scheduledBillingTier: subscriptionInfo.scheduledBillingTier,
    scheduledBillingChangeAt: subscriptionInfo.scheduledBillingChangeAt,
    invoices,
    isLoadingInvoices,
    billingHistoryError,
    isCheckoutLoading,
    checkoutMethodLoading,
    isRestorePurchasesLoading,
    isLoadingSubscription,
    isLegacyWebSubscriber,
    nativeBillingEnabled,
    nativePurchaseFlowEnabled,
    paypalCheckoutEnabled: false,
    webBillingSettingsUrl: WEB_BILLING_SETTINGS_URL,

    // Actions
    startCheckout,
    openCustomerPortal,
    cancelScheduledPlanChange,
    cancelSubscriptionAtPeriodEnd,
    checkSaveOfferEligibility,
    applySaveOffer,
    restoreStorePurchases,
    openWebBillingSettings,
    refreshSubscriptionState,
    recoverExistingMembership,
    syncPayPalSubscription,
    requiresPremium,
    refreshProfile,
  };
}
